import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ContactPicker } from "@/components/inquiries/ContactPicker";
import { useAuth } from "@/context/AuthContext";

import { localToday as today } from "@/lib/inquiry";
const blank = (s) => ({ direction: "inbound", inquiry_date: today(), subject: "", purpose: "", description: "",
  category: s?.categories?.[0] || "", channel: s?.channels?.[0] || "", priority: "medium", entity: "", organisation: "",
  contact_ids: [], primary_contact_id: null, assigned_to: "", custom: {}, next_followup_date: "" });

export const Field = ({ label, children, className = "" }) => (
  <div className={className}><Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>
);

export const Pick = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}</SelectContent>
  </Select>
);

export const CustomFieldInput = ({ f, value, onChange }) => {
  const tid = `custom-${f.key}`;
  if (f.type === "select") return <Pick testid={tid} value={value} onChange={onChange} options={f.options} />;
  if (f.type === "boolean") return <Switch data-testid={tid} checked={!!value} onCheckedChange={onChange} />;
  return <Input data-testid={tid} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
};

export const InquiryFormDialog = ({ open, settings, onClose, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState(blank(settings));
  const [people, setPeople] = useState([]);
  const [entities, setEntities] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF({ ...blank(settings), assigned_to: user?.id || "" });
    api.get("/users/assignable?team=1").then((r) => setPeople(r.data)).catch(() => {});
    api.get("/entities").then((r) => setEntities(r.data.map((e) => e.name))).catch(() => {});
  }, [open, settings, user]);
  if (!settings) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, next_followup_date: f.next_followup_date || null, assigned_to: f.assigned_to || null,
        entity: f.entity || null, organisation: f.organisation || null };
      const { data } = await api.post("/inquiries", body);
      toast.success(`${data.number} registered`);
      window.dispatchEvent(new Event("inquiries:changed"));
      onSaved(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="inquiry-form-dialog">
        <DialogHeader><DialogTitle>Register inquiry</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Type"><Pick testid="inq-direction" value={f.direction} onChange={set("direction")} options={[{ value: "inbound", label: "Inbound — they contacted us" }, { value: "outbound", label: "Outbound — we contacted them" }]} /></Field>
          <Field label="Inquiry date"><Input data-testid="inq-date" type="date" value={f.inquiry_date} max={today()} onChange={set("inquiry_date")} /></Field>
          <Field label="Priority"><Pick testid="inq-priority" value={f.priority} onChange={set("priority")} options={["low", "medium", "high", "urgent"].map((p) => ({ value: p, label: p[0].toUpperCase() + p.slice(1) }))} /></Field>
          <Field label="Subject *" className="col-span-2 md:col-span-3"><Input data-testid="inq-subject" value={f.subject} onChange={set("subject")} /></Field>
          <Field label="Purpose" className="col-span-2 md:col-span-3"><Input data-testid="inq-purpose" value={f.purpose} onChange={set("purpose")} /></Field>
          <Field label="Category"><Pick testid="inq-category" value={f.category} onChange={set("category")} options={settings.categories} /></Field>
          <Field label="Source / channel"><Pick testid="inq-channel" value={f.channel} onChange={set("channel")} options={settings.channels} /></Field>
          <Field label="Assigned to"><Pick testid="inq-assignee" value={f.assigned_to} onChange={set("assigned_to")} options={people.map((p) => ({ value: p.id, label: p.name }))} /></Field>
          <Field label="Party organisation"><Input data-testid="inq-organisation" value={f.organisation} onChange={set("organisation")} placeholder="e.g. Shah & Sons Pvt Ltd" /></Field>
          <Field label="Our entity"><Pick testid="inq-entity" value={f.entity} onChange={set("entity")} options={entities} placeholder="Optional" /></Field>
          <Field label="First follow-up"><Input data-testid="inq-followup" type="date" value={f.next_followup_date} onChange={set("next_followup_date")} /></Field>
          <Field label="Contacts" className="col-span-2 md:col-span-3">
            <ContactPicker value={f.contact_ids} onChange={set("contact_ids")} primary={f.primary_contact_id} onPrimary={set("primary_contact_id")} />
          </Field>
          <Field label="Description" className="col-span-2 md:col-span-3"><Textarea data-testid="inq-description" rows={3} value={f.description} onChange={set("description")} /></Field>
          {settings.custom_fields.map((cf) => (
            <Field key={cf.key} label={`${cf.label}${cf.required ? " *" : ""}`}>
              <CustomFieldInput f={cf} value={f.custom[cf.key]} onChange={(v) => setF((x) => ({ ...x, custom: { ...x.custom, [cf.key]: v } }))} />
            </Field>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="inq-save" disabled={!f.subject.trim() || busy} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Saving…" : "Register inquiry"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
